// Lightweight overlay shown while the client is consuming the seat
// reservation and waiting for the GameRoom's first state. Mirrors the
// RoundEndModal pattern (root = #match-modals, append → fade out → remove).

import { t } from '../i18n';

const STYLES_ID = 'connecting-modal-styles';
const STYLES = `
@import url('https://fonts.googleapis.com/css2?family=Chewy&display=swap');

#match-modals .connecting {
  position: fixed; inset: 0;
  display: flex; align-items: center; justify-content: center;
  background: rgba(0, 0, 0, 0.55);
  backdrop-filter: blur(8px);
  z-index: 200;
  font-family: 'Chewy', ui-sans-serif, system-ui, sans-serif;
  color: #FFFFFF;
  opacity: 0;
  transition: opacity 200ms ease;
}
#match-modals .connecting.shown { opacity: 1; }
#match-modals .connecting.closing { opacity: 0; }

#match-modals .connecting .body {
  display: flex; flex-direction: column; align-items: center; gap: 24px;
  padding: 50px 70px;
  background: rgba(24, 28, 36, 0.7);
  border: 2px solid rgba(255,255,255,0.25);
  border-radius: 30px;
  box-shadow: 0 20px 30px rgba(0,0,0,0.5);
}

#match-modals .connecting .text {
  font-size: 36px;
  text-shadow: 0 -2px 0 rgba(0, 60, 90, 0.35);
}

#match-modals .connecting .spinner {
  width: 320px; height: 18px;
  background: rgba(255,255,255,0.18);
  border-radius: 9px;
  overflow: hidden;
}
#match-modals .connecting .spinner .bar {
  width: 35%; height: 100%;
  background: linear-gradient(90deg, transparent, #FA9C22, transparent);
  animation: connecting-shimmer 1100ms linear infinite;
}
@keyframes connecting-shimmer {
  0%   { transform: translateX(-90%); }
  100% { transform: translateX(290%); }
}
`;

export interface ConnectingModalHandle {
  setText(s: string): void;
  dismiss(): void;
}

export function showConnectingModal(initialText = t('connecting.default')): ConnectingModalHandle {
  ensureStyles();
  const root = ensureRoot();
  const wrap = document.createElement('div');
  wrap.className = 'connecting';
  wrap.innerHTML = `
    <div class="body">
      <div class="text">${escapeHtml(initialText)}</div>
      <div class="spinner"><div class="bar"></div></div>
    </div>
  `;
  root.appendChild(wrap);
  // Trigger the fade-in next frame so the transition fires.
  requestAnimationFrame(() => wrap.classList.add('shown'));

  const textEl = wrap.querySelector('.text') as HTMLElement;
  return {
    setText(s: string): void { textEl.textContent = s; },
    dismiss(): void {
      wrap.classList.add('closing');
      setTimeout(() => wrap.remove(), 220);
    }
  };
}

function ensureRoot(): HTMLElement {
  let root = document.getElementById('match-modals');
  if (!root) {
    root = document.createElement('div');
    root.id = 'match-modals';
    root.style.cssText = `position:fixed; inset:0; z-index:150; pointer-events:none;`;
    document.body.appendChild(root);
  }
  return root;
}

function ensureStyles(): void {
  if (document.getElementById(STYLES_ID)) return;
  const style = document.createElement('style');
  style.id = STYLES_ID;
  style.textContent = STYLES;
  document.head.appendChild(style);
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!);
}
